import React from 'react';
import { getIntl } from '../../../../utils/intl';
import { MedicalDataTypes } from '../../../../primary-record-types';
import { Field } from 'formik';
import { required } from '../../../../utils/validators';
import { SplitFormRow } from '../../../v2BaseComponents/Form';
import PillSelect, { MultiSelectPillSelect } from '../../../v2BaseComponents/Inputs/PillSelect';
import { FormikInputs } from '../../../v2BaseComponents/Inputs';
import Stack, { contentStackSpacing } from '../../../v2BaseComponents/Stack';
import Text from '../../../v2BaseComponents/Text';
import {
    getDefaultRecurrenceEndDate,
    isRecurrenceEndDateValid,
    MAX_SERIES_MONTHS,
} from '../../../../utils/visitRecurrence';

const intl = getIntl('VisitRecurrenceSection', {
    recurring: 'Recurring',
    beta: 'beta',
    doesNotRepeat: 'Does not repeat',
    daily: 'Daily',
    weekly: 'Weekly',
    repeatUntil: 'Repeat Until',
    endDateInvalid: 'Must be within {maxMonths} months of the visit',
    sunday: 'S',
    monday: 'M',
    tuesday: 'T',
    wednesday: 'W',
    thursday: 'T',
    friday: 'F',
    saturday: 'S',
});

const WEEKDAY_OPTIONS: {
    labelKey: 'sunday' | 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday';
    value: string;
}[] = [
    { labelKey: 'sunday', value: '0' },
    { labelKey: 'monday', value: '1' },
    { labelKey: 'tuesday', value: '2' },
    { labelKey: 'wednesday', value: '3' },
    { labelKey: 'thursday', value: '4' },
    { labelKey: 'friday', value: '5' },
    { labelKey: 'saturday', value: '6' },
];

/**
 * Recurrence controls for the visit form (create flows only). Writes
 * resource.recurrence — expansion into occurrences happens at submit time.
 */
const RecurrenceSection: React.FC<{
    values: { resource: Pick<MedicalDataTypes.PrimaryRecordVisit, 'recurrence' | 'startDateTime'> };
    isDisabled: boolean;
    setFieldValue: (field: string, value: any) => void;
}> = ({ values, isDisabled, setFieldValue }) => {
    const recurrence = values.resource.recurrence || null;
    const startDateTime = values.resource.startDateTime;

    const handleFrequencyChange = (frequency: string | null) => {
        if (!frequency || frequency === 'none') {
            setFieldValue('resource.recurrence', null);
            return;
        }
        setFieldValue('resource.recurrence', {
            frequency,
            weekdays: frequency === 'weekly' ? recurrence?.weekdays || null : null,
            endDate: recurrence?.endDate || (startDateTime ? getDefaultRecurrenceEndDate(startDateTime) : ''),
            // Server-side expansion keeps the authored wall-clock across DST.
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || null,
        });
    };

    const validateEndDate = (value: string | undefined) => {
        const requiredError = required(value);
        if (requiredError) return requiredError;
        if (startDateTime && value && !isRecurrenceEndDateValid(startDateTime, value)) {
            return intl('endDateInvalid', { maxMonths: MAX_SERIES_MONTHS });
        }
        return undefined;
    };

    return (
        <Stack vertical fullWidth space={contentStackSpacing}>
            <Stack horizontal align="center" space={1.5}>
                <Text variant="bold-body-1">{intl('recurring')}</Text>
                {/* Lowercase on purpose - signals the feature's development status (Jim). */}
                <Text variant="body-2" color="Gray-Text" testID="visit-recurrence-beta-tag">
                    {intl('beta')}
                </Text>
            </Stack>
            <SplitFormRow>
                <PillSelect
                    options={[
                        { label: intl('doesNotRepeat'), value: 'none', testID: 'visit-recurrence-none' },
                        { label: intl('daily'), value: 'daily', testID: 'visit-recurrence-daily' },
                        { label: intl('weekly'), value: 'weekly', testID: 'visit-recurrence-weekly' },
                    ]}
                    value={recurrence?.frequency || 'none'}
                    onChange={handleFrequencyChange}
                    disableEmpty
                    isDisabled={isDisabled}
                    testID="visit-recurrence-frequency"
                />
            </SplitFormRow>
            {recurrence?.frequency === 'weekly' ? (
                <SplitFormRow>
                    <MultiSelectPillSelect
                        options={WEEKDAY_OPTIONS.map(({ labelKey, value }) => ({
                            label: intl(labelKey),
                            value,
                            testID: `visit-recurrence-weekday-${value}`,
                        }))}
                        value={recurrence.weekdays?.map(String) || null}
                        onChange={(weekdays) =>
                            setFieldValue('resource.recurrence.weekdays', weekdays.length ? weekdays.map(Number) : null)
                        }
                        isDisabled={isDisabled}
                        testID="visit-recurrence-weekdays"
                    />
                </SplitFormRow>
            ) : null}
            {recurrence ? (
                <SplitFormRow>
                    <Field
                        name="resource.recurrence.endDate"
                        isRequired
                        title={intl('repeatUntil')}
                        validate={validateEndDate}
                        component={FormikInputs.DateTimePicker}
                        isDisabled={isDisabled}
                    />
                </SplitFormRow>
            ) : null}
        </Stack>
    );
};

export default React.memo(RecurrenceSection);
